import React, { useRef, useEffect, useCallback } from 'react';
import './RichTextEditor.css';

// A small, dependency-free rich-text field for product descriptions. Supports
// paragraphs (Enter), bold and bullet lists via a tiny toolbar, and produces
// standard HTML (<p>, <strong>, <ul><li>) so it renders natively in the
// customer shop. Pasting is forced to plain text to keep the markup clean.

const BoldIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M7 4h7.2a4.3 4.3 0 0 1 2.7 7.6A4.6 4.6 0 0 1 14.5 20H7V4zm3 2.6v4h3.6a2 2 0 0 0 0-4H10zm0 6.4v4.4h4a2.2 2.2 0 0 0 0-4.4h-4z" />
  </svg>
);
const BulletIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
    <circle cx="4.5" cy="6" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="4.5" cy="12" r="1.4" fill="currentColor" stroke="none" />
    <circle cx="4.5" cy="18" r="1.4" fill="currentColor" stroke="none" />
    <path d="M9 6h11M9 12h11M9 18h11" />
  </svg>
);

const looksEmpty = (html) => {
  if (!html) return true;
  return String(html).replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim() === '';
};

const RichTextEditor = ({ value, onChange, placeholder = '' }) => {
  const ref = useRef(null);

  // Use <p> for new paragraphs (cleaner than <div>) where supported.
  useEffect(() => {
    try { document.execCommand('defaultParagraphSeparator', false, 'p'); } catch (e) { /* ignore */ }
  }, []);

  // Seed the editable from `value` when it changes externally (e.g. the product
  // loads after mount) — but never while the user is typing in it.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (document.activeElement !== el && el.innerHTML !== (value || '')) {
      el.innerHTML = value || '';
    }
  }, [value]);

  const emit = useCallback(() => {
    if (onChange) onChange(ref.current ? ref.current.innerHTML : '');
  }, [onChange]);

  // onMouseDown + preventDefault keeps the selection in the editor so the
  // command applies to it, instead of the toolbar button stealing focus.
  const run = (e, command) => {
    e.preventDefault();
    try { document.execCommand(command, false, null); } catch (err) { /* ignore */ }
    emit();
  };

  const onPaste = (e) => {
    e.preventDefault();
    const text = ((e.clipboardData || window.clipboardData) || {}).getData
      ? (e.clipboardData || window.clipboardData).getData('text/plain')
      : '';
    try { document.execCommand('insertText', false, text); } catch (err) { /* ignore */ }
    emit();
  };

  return (
    <div className="rte">
      <div className="rte-toolbar">
        <button type="button" className="rte-btn" title="Bold" aria-label="Bold" onMouseDown={(e) => run(e, 'bold')}><BoldIcon /></button>
        <button type="button" className="rte-btn" title="Bullet list" aria-label="Bullet list" onMouseDown={(e) => run(e, 'insertUnorderedList')}><BulletIcon /></button>
      </div>
      <div className="rte-body">
        <div
          ref={ref}
          className="rte-content"
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          onInput={emit}
          onBlur={emit}
          onPaste={onPaste}
        />
        {looksEmpty(value) && <div className="rte-placeholder" aria-hidden>{placeholder}</div>}
      </div>
    </div>
  );
};

export default RichTextEditor;
